import {Modal} from '@/components/ui/modal/Modal'
import {StyledBody, StyledMediaWrapper} from './style'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {MouseEvent, useState} from 'react'
import {MediaTypeE} from '@/types'
import {FileUploader} from '@/components/ui/file-uploader/FileUploader'
import {EditMediaUploadArea} from './edit-media-upload-area/EditMediaUploadArea'
import {AxiosRequestConfig} from 'axios'
import {ACCEPT_IMAGE_FILE_TYPES, ACCEPT_VIDEO_FILE_TYPES} from '@/utilities/constants/fileUploader'
import {PreviewMedia} from './preview-media/PreviewMedia'
import {useHandleError} from '@/hooks/useHandleError'
import {GoalMedia, GoalV3} from '@/features/chat/chat.model'
import {useEditGoalV3} from '../../queries/useEditGoalV3'
import {useUploadMedia} from '@/hooks/useUploadMedia'
import {ScopeUploadFileE} from '@/features/chat/services/chat.http'

type EditMediaModalProps = {
    onClose: () => void
    goal?: Partial<GoalV3>
}

export const EditMediaModal = ({onClose, goal}: EditMediaModalProps) => {
    const {t} = useTranslation()
    const defaultMediaList = goal?.media ?? []
    const [mediaList, setMediaList] = useState<GoalMedia[]>(defaultMediaList)
    const [file, setFile] = useState<File | undefined>()
    const disabled = !mediaList.length

    const onSuccess = () => {
        onClose()
    }

    const {mutate: editGoal, isPending, isError, error} = useEditGoalV3(onSuccess)

    useHandleError({isError, error, isBlankPage: true})

    const onRemove = (e: MouseEvent<Element, globalThis.MouseEvent>, id: number) => {
        e.stopPropagation()
        setMediaList(mediaList?.filter(item => item.id !== id))
    }

    const handleSubmit = () => {
        const addMediaList =
            mediaList?.filter(media => !('isMain' in media))?.map(media => ({fileUploadId: media.id})) ?? []
        const removeMediaList =
            defaultMediaList
                .filter(defaultMedia => !mediaList.find(media => defaultMedia.id === media.id))
                ?.map(media => ({goalMediaId: media.id})) ?? []
        if (goal?.id && goal?.endDate)
            editGoal({
                goalId: goal.id.toString(),
                endDate: new Date(goal?.endDate),
                goalMedias: [...addMediaList, ...removeMediaList]
            })
    }

    const {uploadFile} = useUploadMedia({scope: ScopeUploadFileE.GOAL_MEDIA})
    const sendFile = async ({file, options}: {file: File; options: AxiosRequestConfig}) => {
        setFile(file)
        const uploadedFile = await uploadFile({blob: file, options})

        setMediaList([
            ...mediaList,
            {
                id: uploadedFile.fileUploadId,
                url: URL.createObjectURL(file),
                type:
                    (uploadedFile.type as unknown as MediaTypeE) === MediaTypeE.PHOTO
                        ? MediaTypeE.IMAGE
                        : MediaTypeE.VIDEO,
                pathLocation: uploadedFile.url,
                goalId: +`${goal?.id}`
            }
        ])
    }

    return (
        <Modal onClose={onClose} minHeight="527px" title={t('edit_goal_media:title')}>
            {isPending && <Spinner />}
            <StyledBody>
                <StyledMediaWrapper>
                    {[...mediaList, {id: Date.now(), type: null}]?.map((media, index) => {
                        const isImage = media?.type === MediaTypeE.IMAGE
                        return media?.type ? (
                            <PreviewMedia
                                key={media.id}
                                onRemove={(e: MouseEvent<Element, globalThis.MouseEvent>) => onRemove(e, media.id)}
                                url={media.url}
                                isImage={isImage}
                            />
                        ) : (
                            <FileUploader
                                key={index}
                                UploadArea={EditMediaUploadArea}
                                uploadFunction={sendFile}
                                accept={{...ACCEPT_VIDEO_FILE_TYPES, ...ACCEPT_IMAGE_FILE_TYPES}}
                                onRemove={() => setFile(undefined)}
                                isError={false}
                                isSuccessfulUpload={!!file}
                            />
                        )
                    })}
                </StyledMediaWrapper>
                <Button onClick={handleSubmit} disabled={disabled}>
                    {t('common:save')}
                </Button>
            </StyledBody>
        </Modal>
    )
}
